import React, { createContext, useContext, ReactNode, lazy, Suspense, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntityModal } from '@/hooks/useEntityModal';
import { EntityModal } from '@/components/ui/entity-modal';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { PricingPolicyDetails } from '@/pages/pricing-policies/pricing-policy-details';
import { LimitPolicyDetails } from '@/pages/limit-policies/limit-policy-details';
import { ScenarioDetails } from '@/pages/scenarios/scenario-details';
import { UserDetails } from '@/pages/users/user-details';
import { VoucherDetails } from '@/pages/vouchers/voucher-details';
import { CampaignDetails } from '@/pages/campaigns/campaign-details';
import { VehicleDetails } from '@/pages/vehicles/components/vehicle-details';
// CustomerDetails is lazy loaded to avoid circular dependency
// (CustomerDetails imports EntityDetailField which imports useEntityModalContext)
const CustomerDetails = lazy(() => import('@/pages/customers/customer-details').then(m => ({ default: m.CustomerDetails })));
import { ContractDetailsQuery } from '@/pages/contracts/contract-details-query';
import { ParkingSessionDetailsQuery } from '@/pages/parking-sessions/parking-session-details-query';

interface EntityModalContextType {
	openEntityModal: (entityType: string, entityId: string) => void;
	closeEntityModal: () => void;
	goBackModal: () => void;
	hasParentModal: boolean;
}

const EntityModalContext = createContext<EntityModalContextType | undefined>(undefined);

interface EntityModalProviderProps {
	children: ReactNode;
}

// Entity detail component mapping - use a getter function to avoid circular dependency issues
// CustomerDetails is lazy loaded, so we need to defer access to it
export const getEntityDetailComponent = (entityType: string): React.ComponentType<any> | undefined => {
	const components: Record<string, React.ComponentType<any>> = {
		pricingPolicy: PricingPolicyDetails,
		pricingPolicies: PricingPolicyDetails,
		limitPolicy: LimitPolicyDetails,
		limitPolicies: LimitPolicyDetails,
		scenario: ScenarioDetails,
		scenarios: ScenarioDetails,
		user: UserDetails,
		users: UserDetails,
		campaign: CampaignDetails,
		campaigns: CampaignDetails,
		voucher: VoucherDetails,
		vouchers: VoucherDetails,
		vehicle: VehicleDetails,
		vehicles: VehicleDetails,
		customer: CustomerDetails,
		customers: CustomerDetails,
		contract: ContractDetailsQuery,
		contracts: ContractDetailsQuery,
		parkingSession: ParkingSessionDetailsQuery,
		parkingSessions: ParkingSessionDetailsQuery,
	};
	return components[entityType];
};

// Entity type display names
export const ENTITY_DISPLAY_NAMES: Record<string, string> = {
	rate: 'Rate',
	rates: 'Rate',
	pricingPolicy: 'Pricing Policy',
	pricingPolicies: 'Pricing Policy',
	limitPolicy: 'Limit Policy',
	limitPolicies: 'Limit Policy',
	scenario: 'Scenario',
	scenarios: 'Scenario',
	user: 'User',
	users: 'User',
	campaign: 'Campaign',
	campaigns: 'Campaign',
	voucher: 'Voucher',
	vouchers: 'Voucher',
	limit: 'Limit',
	limits: 'Limit',
	vehicle: 'Vehicle',
	vehicles: 'Vehicle',
	customer: 'Customer',
	customers: 'Customer',
	contract: 'Contract',
	contracts: 'Contract',
	facility: 'Facility',
	facilities: 'Facility',
	parkingSession: 'Parking Session',
	parkingSessions: 'Parking Session',
	// Add more entity types as needed
};

// Map entity types to their ID prop names (to handle singular/plural differences)
export const ENTITY_ID_PROP_NAMES: Record<string, string> = {
	rate: 'rateId',
	rates: 'rateId',
	pricingPolicy: 'pricingPolicyId',
	pricingPolicies: 'pricingPolicyId',
	limitPolicy: 'limitPolicyId',
	limitPolicies: 'limitPolicyId',
	scenario: 'scenarioId',
	scenarios: 'scenarioId',
	user: 'userId',
	users: 'userId',
	campaign: 'campaignId',
	campaigns: 'campaignId',
	voucher: 'voucherId',
	vouchers: 'voucherId',
	limit: 'limitId',
	limits: 'limitId',
	vehicle: 'vehicleId',
	vehicles: 'vehicleId',
	customer: 'customerId',
	customers: 'customerId',
	contract: 'contractId',
	contracts: 'contractId',
	facility: 'facilityId',
	facilities: 'facilityId',
	parkingSession: 'parkingSessionId',
	parkingSessions: 'parkingSessionId',
};

// Map entity types to their resource paths for navigation
export const ENTITY_RESOURCE_PATHS: Record<string, string> = {
	rate: '/rates',
	rates: '/rates',
	pricingPolicy: '/pricing-policies',
	pricingPolicies: '/pricing-policies',
	limitPolicy: '/limit-policies',
	limitPolicies: '/limit-policies',
	scenario: '/scenarios',
	scenarios: '/scenarios',
	user: '/users',
	users: '/users',
	campaign: '/campaigns',
	campaigns: '/campaigns',
	voucher: '/vouchers',
	vouchers: '/vouchers',
	limit: '/limits',
	limits: '/limits',
	vehicle: '/vehicles',
	vehicles: '/vehicles',
	customer: '/customers',
	customers: '/customers',
	contract: '/contracts',
	contracts: '/contracts',
	facility: '/facilities',
	facilities: '/facilities',
	parkingSession: '/parking-sessions',
	parkingSessions: '/parking-sessions',
};

export function EntityModalProvider({ children }: EntityModalProviderProps) {
	const { modalState, openEntityModal, closeEntityModal, goBackModal, getCurrentModal, hasParentModal } = useEntityModal();
	const navigate = useNavigate();

	// Navigation handlers for edit and clone actions
	const handleEdit = useCallback((entityType: string, id: string) => {
		const resourcePath = ENTITY_RESOURCE_PATHS[entityType];
		if (resourcePath) {
			closeEntityModal();
			navigate(`${resourcePath}/edit/${id}`);
		}
	}, [closeEntityModal, navigate]);

	const handleClone = useCallback((entityType: string, id: string) => {
		const resourcePath = ENTITY_RESOURCE_PATHS[entityType];
		if (resourcePath) {
			closeEntityModal();
			navigate(`${resourcePath}/clone/${id}`);
		}
	}, [closeEntityModal, navigate]);

	const renderEntityDetails = () => {
		const currentModal = getCurrentModal();
		if (!modalState.isOpen || !currentModal) {
			return null;
		}

		const DetailComponent = getEntityDetailComponent(currentModal.entityType);

		if (!DetailComponent) {
			return (
				<div className="text-center py-8">
					<p className="text-muted-foreground">
						No detail component found for entity type: {currentModal.entityType}
					</p>
				</div>
			);
		}

		// Create props object with the correct ID property name
		const idPropName = ENTITY_ID_PROP_NAMES[currentModal.entityType] || `${currentModal.entityType}Id`;
		const props: Record<string, unknown> = {
			[idPropName]: currentModal.entityId,
			showHeader: true,  // Show the entity header with icon/name/status
			onEdit: (id: string) => handleEdit(currentModal.entityType, id),
			onClone: (id: string) => handleClone(currentModal.entityType, id),
			onOpenInNewWindow: (id: string) => openEntityInNewWindow(currentModal.entityType, id),
			onBack: hasParentModal ? goBackModal : undefined,
			showBackButton: hasParentModal,
			// Header close should always close the modal.
			onClose: closeEntityModal,
			// Note: onDelete is intentionally omitted in modals for safety
		};

		// Wrap in Suspense for lazy-loaded components (e.g., CustomerDetails)
		return (
			<Suspense fallback={<div className="py-8 text-center text-muted-foreground">Loading...</div>}>
				<DetailComponent {...props} />
			</Suspense>
		);
	};

	const currentModal = getCurrentModal();
	const displayName = currentModal
		? ENTITY_DISPLAY_NAMES[currentModal.entityType] || currentModal.entityType
		: '';

	return (
		<EntityModalContext.Provider value={{ openEntityModal, closeEntityModal, goBackModal, hasParentModal }}>
			{children}
			<EntityModal
				isOpen={modalState.isOpen}
				onClose={closeEntityModal}
				onBack={hasParentModal ? goBackModal : undefined}
				entityType={currentModal?.entityType || ''}
				title={`${displayName} Details`}
				maxWidth="3xl"
				showBackButton={hasParentModal}
				hideTitle={true}
				hideCloseButton={true}
			>
				{renderEntityDetails()}
			</EntityModal>
		</EntityModalContext.Provider>
	);
}

export function useEntityModalContext() {
	const context = useContext(EntityModalContext);
	if (context === undefined) {
		throw new Error('useEntityModalContext must be used within an EntityModalProvider');
	}
	return context;
}
